import { useEffect, useRef, useState } from "react";
import { approvePurchaseOrder, exportPO } from "../../api/purchaseOrders";
import { getExpenseAccountList } from "../../api/accounting";
import { getCatalogue } from "../../api/common/catalogues.ts";
import { handleError } from "../../services/errorHandler";

export const MATERIAL_CONSUMABLE_CODES = ["POTYPE_MATERIAL", "POTYPE_CONSUMABLE"];
export const SERVICE_TOOLS_CODES = ["POTYPE_SERVICE", "POTYPE_TOOLS", "POTYPE_ASSET"];

export const PDF_OPTIONS = [
  { label: "Descargar PDF con precios", showPrices: true, icon: "monetization_on" },
  { label: "Descargar PDF sin precios", showPrices: false, icon: "download" },
];

export const buildDefaultItems = (po) => {
  if (Array.isArray(po.items) && po.items.length > 0) {
    return po.items;
  }
  return new Array(Number(po.noConcepts) || 1).fill({
    quantity: "",
    price: "",
    productId: null,
    unitId: "",
    concept: "",
    workOrderId: "",
    assetTypeId: "",
    assetId: "",
    taxRate: po.isImport ? 0 : 16,
    subtotal: 0,
  });
};

export function usePODetailCatalogs(POdata, companyId) {
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [units, setUnits] = useState([]);
  const [loadingExpenseAccounts, setLoadingExpenseAccounts] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);

  useEffect(() => {
    const fetchExpenseAccounts = async () => {
      setLoadingExpenseAccounts(true);
      const activityList = POdata.provider?.activities;
      const expenseAccountsIds = activityList?.map((e) => e.expenseAccountId);
      const response = await getExpenseAccountList({
        companyId,
        ExpenseAccountIds: expenseAccountsIds,
        pageSize: 300,
      });
      setExpenseAccounts(response.data);
      setLoadingExpenseAccounts(false);
    };

    const fetchUnits = async () => {
      setLoadingUnits(true);
      const response = await getCatalogue({ CatalogueName: "UNIT", pageSize: 1000 });
      setUnits(response.data);
      setLoadingUnits(false);
    };

    fetchExpenseAccounts();
    fetchUnits();
  }, [POdata.provider, companyId]);

  return {
    expenseAccounts,
    units,
    loadingExpenseAccounts,
    loadingUnits,
    catalogsLoading: loadingExpenseAccounts || loadingUnits,
  };
}

export function usePODetailPdfMenu(id, POdata, openSnack) {
  const [pdfMenuOpen, setPdfMenuOpen] = useState(false);
  const [selectedPdfIndex, setSelectedPdfIndex] = useState(0);
  const pdfAnchorRef = useRef(null);

  const handleFileDownload = async (showPrices) => {
    try {
      const response = await exportPO(id, showPrices);
      const contentDisposition = response.headers["content-disposition"];
      let fileName = POdata.code || "ordencompra";

      if (contentDisposition && contentDisposition.indexOf("attachment") !== -1) {
        const matches = contentDisposition.match(/filename="([^"]*)"/);
        if (matches && matches[1]) {
          fileName = matches[1];
        }
      }

      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      openSnack(
        "No se pudo generar el PDF, verifica que el proveedor tenga contacto y dirección",
        "error",
      );
    }
  };

  const handlePdfClick = () => {
    handleFileDownload(PDF_OPTIONS[selectedPdfIndex].showPrices);
  };

  const handlePdfMenuItemClick = (event, index) => {
    setSelectedPdfIndex(index);
    setPdfMenuOpen(false);
  };

  const handlePdfToggle = () => {
    setPdfMenuOpen((prevOpen) => !prevOpen);
  };

  const handlePdfClose = (event) => {
    if (pdfAnchorRef.current && pdfAnchorRef.current.contains(event.target)) {
      return;
    }
    setPdfMenuOpen(false);
  };

  return {
    pdfMenuOpen,
    selectedPdfIndex,
    pdfAnchorRef,
    handlePdfClick,
    handlePdfMenuItemClick,
    handlePdfToggle,
    handlePdfClose,
  };
}

export function createHandleApprove(id, openSnack) {
  return async () => {
    try {
      await approvePurchaseOrder(id);
      openSnack("Órden de compra aprobada exitosamente", "success");
    } catch (e) {
      openSnack(handleError(e), "error");
    }
  };
}
